import { useState, useCallback, useEffect } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import JSZip from "jszip";
import { toast } from "sonner";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, Copy } from "lucide-react";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { buildIco, buildWebManifest, FAVICON_FILES, FAVICON_HTML } from "./icoUtils";

const TOOL_INFO_CONTENT = {
  what: `Generates a complete favicon package from one image: favicon.ico (16, 32 and 48 px), PNGs at 16, 32, 48, 180 (Apple touch icon), 192 and 512 (Android/PWA), a site.webmanifest, plus the HTML tags to paste into your site (including the manifest and the 192/512 icons). The Apple touch icon is always filled with the background colour (white by default).`,
  how: `Upload a square-ish image, choose how it fills the square (crop to fill or fit whole image), optionally add a background colour, then download the ZIP or individual files. Everything is processed in your browser.`,
  tip: `Favicons are shown tiny, so a simple, high-contrast mark works best. Use "Fit whole image" with a background colour for logos that shouldn't be cropped.`,
};

function render(img: HTMLImageElement, size: number, mode: "cover" | "contain", bg: string | null): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, size, size); }
  const w = img.naturalWidth, h = img.naturalHeight;
  const s = (mode === "cover" ? Math.max : Math.min)(size / w, size / h);
  ctx.drawImage(img, (size - w * s) / 2, (size - h * s) / 2, w * s, h * s);
  return c;
}

const toPng = (c: HTMLCanvasElement) =>
  new Promise<Uint8Array>((res, rej) =>
    c.toBlob(async b => (b ? res(new Uint8Array(await b.arrayBuffer())) : rej(new Error("encode failed"))), "image/png"));

export default function FaviconGenerator() {
  const { gateShare } = useShareGate();
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [mode, setMode] = useState<"cover" | "contain">("cover");
  const [useBg, setUseBg] = useState(false);
  const [bg, setBg] = useState("#ffffff");
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const { logUsageNow } = useLogUsageOnAction();
  // iOS draws transparent apple-touch-icons on black, so that one file is always filled (white when no colour is chosen).
  const appleBg = useBg ? bg : "#ffffff";

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    const url = URL.createObjectURL(f);
    const i = new Image();
    i.onload = () => setImg(i);
    i.onerror = () => toast.error("Could not read that image");
    i.src = url;
  }, []);

  useEffect(() => {
    if (!img) return;
    const p: Record<number, string> = {};
    for (const s of [16, 32, 48, 180]) p[s] = render(img, s, mode, s === 180 ? appleBg : useBg ? bg : null).toDataURL();
    setPreviews(p);
  }, [img, mode, useBg, bg, appleBg]);

  const build = async () => {
    const files: { name: string; data: Uint8Array }[] = [];
    for (const f of FAVICON_FILES) files.push({ name: f.name, data: await toPng(render(img!, f.size, mode, f.name === "apple-touch-icon.png" ? appleBg : useBg ? bg : null)) });
    const ico = buildIco([16, 32, 48].map(size => ({ size, png: files.find(x => x.name === `favicon-${size}x${size}.png`)!.data })));
    files.push({ name: "favicon.ico", data: ico });
    return files;
  };

  const downloadZip = async () => {
    if (!img || !(await logUsageNow())) return;
    const files = await build();
    const zip = new JSZip();
    files.forEach(f => zip.file(f.name, f.data));
    zip.file("site.webmanifest", buildWebManifest(appleBg));
    zip.file("README.html-snippet.txt", FAVICON_HTML + "\n");
    downloadBlob(await zip.generateAsync({ type: "blob" }), "favicons.zip");
  };

  const downloadIco = async () => {
    if (!img || !(await logUsageNow())) return;
    const files = await build();
    downloadBlob(new Blob([files.find(f => f.name === "favicon.ico")!.data as BlobPart], { type: "image/x-icon" }), "favicon.ico");
  };

  return (
    <ToolContainer>
      {!img ? (
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image" description="Generate a full favicon set from any image" />
      ) : (
        <div className="space-y-5 max-w-xl mx-auto">
          <div className="flex items-end justify-center gap-5 p-4 border rounded-lg">
            {[16, 32, 48, 180].map(s => (
              <div key={s} className="text-center text-xs text-muted-foreground">
                {previews[s] && <img src={previews[s]} alt={`${s}px`} style={{ width: Math.min(s, 96), height: Math.min(s, 96), imageRendering: s < 48 ? "pixelated" : "auto" }} className="border mx-auto mb-1" />}
                {s === 180 ? "Apple 180" : `${s}px`}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <Label>Fill</Label>
            <Button size="sm" variant={mode === "cover" ? "default" : "outline"} onClick={() => setMode("cover")}>Crop to fill</Button>
            <Button size="sm" variant={mode === "contain" ? "default" : "outline"} onClick={() => setMode("contain")}>Fit whole image</Button>
            <label className="flex items-center gap-2 text-sm ml-2">
              <input type="checkbox" checked={useBg} onChange={e => setUseBg(e.target.checked)} /> Background
            </label>
            {useBg && <input type="color" aria-label="Background colour" value={bg} onChange={e => setBg(e.target.value)} className="h-8 w-8 rounded border" />}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={downloadZip} className="gap-2"><Download className="h-4 w-4" /> Download all (ZIP)</Button>
            <Button variant="outline" onClick={downloadIco}>favicon.ico only</Button>
            <Button variant="ghost" onClick={() => { setImg(null); }}>Different image</Button>
          </div>
          <p className="text-xs text-muted-foreground">The Apple touch icon always gets a solid background (white if you do not pick a colour), because iOS shows transparent icons on black. The ZIP also includes site.webmanifest; set its name and short_name before use.</p>
          <div>
            <Label className="text-xs">Add to your site's &lt;head&gt;</Label>
            <pre className="mt-1 p-3 bg-muted/40 rounded text-xs overflow-x-auto">{FAVICON_HTML}</pre>
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(FAVICON_HTML); toast.success("Copied"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>
              <Copy className="h-3.5 w-3.5" /> Copy tags
            </Button>
          </div>
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
